/* ============================================================
   2f9c70ba: pure referral view-model — URL capture, stash and
   display helpers. No React, no IO beyond localStorage params
   (injected): node-tested like the sibling *View modules.
   ============================================================ */

/** localStorage key for the stashed ?ref attribution token. */
export const REF_STORAGE_KEY = 'pm_referral';

/** Referral tokens: 2–64 chars of the slug alphabet (code or slug). */
const REF_TOKEN_RE = /^[a-z0-9]([a-z0-9-]{0,62}[a-z0-9])?$/;

export interface StashedRef {
  code: string;
  ts: number;
}

/** Parse the ?ref= param (case-normalized); null when absent/invalid. */
export function parseRefParam(search: string): string | null {
  const params = new URLSearchParams(search);
  const raw = (params.get('ref') ?? '').trim().toLowerCase();
  if (raw.length < 2 || raw.length > 64) return null;
  return REF_TOKEN_RE.test(raw) ? raw : null;
}

/** Normalize a raw slug/ref input like the backend: trim + lowercase. */
export function normalizeSlugInput(input: string): string {
  return (input ?? '').trim().toLowerCase();
}

/** Mirror of the backend slug charset check (without settings bounds). */
export function slugCharsetOk(slug: string): boolean {
  return REF_TOKEN_RE.test(slug);
}

/** Client-side pre-validation for the slug editor (backend stays the gate). */
export function validateSlugLocal(
  slug: string,
  limits: { min: number; max: number },
): string | null {
  const s = normalizeSlugInput(slug);
  if (s.length === 0) return null; // empty = release, allowed
  if (s.length < limits.min || s.length > limits.max)
    return `Адрес: ${limits.min}–${limits.max} символов.`;
  if (!slugCharsetOk(s)) return 'Адрес: строчные латинские буквы, цифры и дефисы.';
  return null;
}

/** Build the shareable ?ref= link for a code. */
export function buildReferralLink(origin: string, code: string): string {
  return `${origin.replace(/\/+$/, '')}/?ref=${code}`;
}

/** Store-side helpers: thin wrappers so tests can inject a fake store. */
export interface RefStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Stash a captured ref token (keeps the earliest capture, refresh ts). */
export function stashRef(storage: RefStorage, code: string, now = Date.now()): void {
  storage.setItem(REF_STORAGE_KEY, JSON.stringify({ code, ts: now } satisfies StashedRef));
}

/** Read the stashed ref token; null when absent or malformed. */
export function readStashedRef(storage: RefStorage): StashedRef | null {
  try {
    const raw = storage.getItem(REF_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StashedRef>;
    if (typeof parsed.code !== 'string' || !REF_TOKEN_RE.test(parsed.code)) return null;
    return { code: parsed.code, ts: typeof parsed.ts === 'number' ? parsed.ts : 0 };
  } catch {
    return null;
  }
}

/** Drop the stash (e.g. after the attribution is safely attached to an account). */
export function clearStashedRef(storage: RefStorage): void {
  storage.removeItem(REF_STORAGE_KEY);
}

/** Badge copy for the referring performer in match lists. */
export function referredBadgeText(isReferred: boolean | undefined): string | null {
  return isReferred ? 'Рекомендуем' : null;
}

/** Format the referral commission sums for the cabinet card. */
export function referralTotals(ref: {
  ownShopCommissionRub: number;
  otherShopCommissionRub: number;
}): string {
  const fmt = (v: number) =>
    `${v.toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽`;
  return `Свой магазин: ${fmt(ref.ownShopCommissionRub)} · Чужие: ${fmt(ref.otherShopCommissionRub)}`;
}
